import React, { useState } from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { Theme } from 'ui_system/theme';
import { ReactModal } from 'ui_system/modals/ReactModal';
import WarningIcon from 'views/ui/icons/Warning';

const Wrapper = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  height: 100vh;
  overflow-y: hidden;
  text-align: left;
  width: 100%;

  @media (min-width: ${Theme.mobile.phoneWidth}px) {
    height: auto;
  }
`;

const HeaderWrapper = styled.div`
  padding: 20px;
  @media (min-width: ${Theme.mobile.phoneWidth}px) {
    padding: 0;
  }
`;

const Header = styled.div`
  font-size: 19px;
  font-weight: 700;
  text-align: center;
`;

const StyledWarningIcon = styled(WarningIcon)`
  fill: ${Theme.colors.orange};
  height: 32px;
  width: 32px;
`;

const ErrorWrapper = styled.div`
  align-items: center;
  display: flex;
  margin-top: 18px;
`;

const ErrorMessage = styled.div`
  color: #666;
  font-size: 16px;
  line-height: 1.2;
  margin-left: 15px;
`;

const TimeSlotList = styled.ul`
  background-color: ${Theme.colors.backgroundColor};
  overflow-y: auto;
  width: 100%;

  @media (min-width: ${Theme.mobile.phoneWidth}px) {
    margin-top: 20px;
    max-height: 200px;
    overflow-y: scroll;
  }
`;

const TimeSlot = styled.li`
  font-size: 16px;
  color: #000;
  font-weight: 700;
  margin: 15px 0 0 32px;
  width: 200px;

  &:last-of-type {
    margin-bottom: 15px;
  }
`;

const Button = styled.button`
  display: none;

  @media (min-width: ${Theme.mobile.phoneWidth}px) {
    background-color: transparent;
    border-radius: 4px;
    border: 1px solid ${Theme.colors.line};
    display: block;
    font-size: 16px;
    height: 45px;
    margin-top: 35px;
    width: 150px;
  }
`;

const MAX_TIME_SLOTS_TO_DISPLAY = 50;
const TimeSlotErrorView = ({ error = {}, onClose }) => {
  const { title, meta = {} } = error || {};
  const { time_slots: timeSlots = [] } = meta || {};
  const totalTimeSlots = timeSlots.length;
  const someTimeSlots = timeSlots.slice(0, MAX_TIME_SLOTS_TO_DISPLAY);
  return (
    <>
      <Wrapper data-testid="time-slot-error-modal">
        <HeaderWrapper>
          <Header>
            Sorry, we encountered a problem. Please reach out to the support
            team for assistance.
          </Header>
          {title && (
            <ErrorWrapper>
              <StyledWarningIcon />
              <ErrorMessage>{error.title}</ErrorMessage>
            </ErrorWrapper>
          )}
        </HeaderWrapper>
        {someTimeSlots.length > 0 && (
          <TimeSlotList data-testid="time-slot-list">
            {someTimeSlots.map((time) => (
              <TimeSlot>{time}</TimeSlot>
            ))}
            {totalTimeSlots > MAX_TIME_SLOTS_TO_DISPLAY && (
              <TimeSlot>
                Plus {totalTimeSlots - MAX_TIME_SLOTS_TO_DISPLAY} other
                timeslot(s)
              </TimeSlot>
            )}
          </TimeSlotList>
        )}
        <Button onClick={onClose} data-testid="Ok-btn">
          Ok
        </Button>
      </Wrapper>
    </>
  );
};

TimeSlotErrorView.propTypes = {
  error: PropTypes.object.isRequired,
};

const StyledTimeSlotErrorView = styled(ReactModal(TimeSlotErrorView))`
  padding: 0;
`;

export const TimeSlotErrorViewModal = ({ error, onClose }) => {
  const [modalOpen, setModalOpen] = useState(true);
  return (
    <StyledTimeSlotErrorView
      error={error}
      isOpen={modalOpen}
      onClose={() => {
        onClose();
        setModalOpen(false);
      }}
    />
  );
};
